import React, { useCallback, useMemo, useReducer } from 'react';
import { createPortal } from 'react-dom';
import { ToastContext, type Toast, type ToastVariant } from '../contexts/ToastContext';


type ToastContextValue = {
  show: (toast: Omit<Toast, 'id'>) => string;
  success: (message: string, opts?: Omit<Toast, 'id' | 'message' | 'variant'>) => string;
  error: (message: string, opts?: Omit<Toast, 'id' | 'message' | 'variant'>) => string;
  info: (message: string, opts?: Omit<Toast, 'id' | 'message' | 'variant'>) => string;
  warning: (message: string, opts?: Omit<Toast, 'id' | 'message' | 'variant'>) => string;
  dismiss: (id: string) => void;
};


type State = { toasts: Toast[] };
type Action =
    | { type: 'ADD'; toast: Toast }
    | { type: 'REMOVE'; id: string }
    | { type: 'CLEAR' };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'ADD':    return { toasts: [action.toast, ...state.toasts] };
    case 'REMOVE': return { toasts: state.toasts.filter(t => t.id !== action.id) };
    case 'CLEAR':  return { toasts: [] };
    default:       return state;
  }
}

function generateId(): string {
  return Math.random().toString(36).slice(2, 10);
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, { toasts: [] });

  const dismiss = useCallback((id: string) => {
    dispatch({ type: 'REMOVE', id });
  }, []);

  const show = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = generateId();
    const full: Toast = {
      id,
      duration: toast.duration ?? 3500,
      variant:  toast.variant ?? 'info',
      ...toast,
    };
    dispatch({ type: 'ADD', toast: full });
    if (full.duration && full.duration > 0) {
      window.setTimeout(() => dismiss(id), full.duration);
    }
    return id;
  }, [dismiss]);

  const value = useMemo<ToastContextValue>(() => {
    const mk = (variant: ToastVariant) =>
        (message: string, opts?: Omit<Toast, 'id' | 'message' | 'variant'>) =>
            show({ message, variant, ...opts });

    return {
      show,
      dismiss,
      success: mk('success'),
      error:   mk('error'),
      info:    mk('info'),
      warning: mk('warning'),
    };
  }, [show, dismiss]);

  return (
      <ToastContext.Provider value={value}>
        {children}
        <ToastContainer toasts={state.toasts} onClose={dismiss} />
      </ToastContext.Provider>
  );
}



function ToastContainer({ toasts, onClose }: { toasts: Toast[]; onClose: (id: string) => void }) {
  if (typeof document === 'undefined') return null;
  return createPortal(
      <div aria-live="polite" aria-atomic="true"
           className="pointer-events-none fixed inset-0 z-[9999] flex flex-col items-end gap-2 p-4 sm:p-6">
        <div className="ml-auto flex w-full max-w-sm flex-col gap-2">
          {toasts.map(t => (
              <ToastItem key={t.id} toast={t} onClose={() => onClose(t.id)} />
          ))}
        </div>
      </div>,
      document.body
  );
}

function ToastItem({ toast, onClose }: { toast: Toast; onClose: () => void }) {
  const colors: Record<ToastVariant, string> = {
    success: 'border-green-300 bg-green-50 text-green-900',
    error:   'border-red-300 bg-red-50 text-red-900',
    info:    'border-blue-300 bg-blue-50 text-blue-900',
    warning: 'border-yellow-300 bg-yellow-50 text-yellow-900',
  };
  const icon: Record<ToastVariant, string> = {
    success: '✓',
    error:   '⨉',
    info:    'ℹ︎',
    warning: '⚠︎',
  };

  return (
      <div role="status"
           className={`pointer-events-auto relative flex w-full items-start gap-3 rounded-lg border p-3 shadow-lg ring-1 ring-black/5 ${colors[toast.variant ?? 'info']}`}>
        <div className="mt-0.5 select-none">{icon[toast.variant ?? 'info']}</div>
        <div className="min-w-0 flex-1">
          {toast.title && <p className="text-sm font-medium">{toast.title}</p>}
          <p className="text-sm">{toast.message}</p>
        </div>
        <button type="button" onClick={onClose} aria-label="Dismiss"
                className="rounded p-1 text-current/70 hover:text-current focus:outline-none focus:ring-2 focus:ring-offset-2">
          ✕
        </button>
      </div>
  );
}